import { forwardRef, type ElementRef, type ComponentPropsWithoutRef } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import SearchIcon from '@discover-ui/icons/search.svg';
import XCircleIcon from '@discover-ui/icons/x-circle.svg';
import { cn } from '@discover-lib/utils';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';

export type CommandInputProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
  clearable?: boolean;
  rootClassName?: string;
};

const CommandInput = forwardRef<ElementRef<typeof CommandPrimitive.Input>, CommandInputProps>(
  ({ className, clearable = false, rootClassName, value, ...props }, ref) => {
    const { track } = useAnalytics();
    const trackAndChange = (search: string) => {
      track('filters_searched', {
        search_value: search,
      });

      props.onValueChange?.(search);
    };
    const showClear = clearable && typeof value === 'string' && value.length > 0;

    if (!clearable) {
      return (
        <div className={cn('flex items-center px-3', rootClassName)} cmdk-input-wrapper="">
          <SearchIcon className="mr-2 size-5 shrink-0 opacity-50" />
          <CommandPrimitive.Input
            ref={ref}
            value={value}
            className={cn(
              'flex h-12 w-full rounded-md border-input bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
              className
            )}
            {...props}
            onValueChange={trackAndChange}
          />
        </div>
      );
    }

    return (
      <div className={cn('px-3', rootClassName)} cmdk-input-wrapper="">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 opacity-50" />
          <CommandPrimitive.Input
            ref={ref}
            value={value}
            className={cn(
              'flex h-12 w-full rounded-md border-input bg-transparent py-3 pl-10 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
              showClear && 'pr-10',
              className
            )}
            {...props}
            onValueChange={trackAndChange}
          />
          {showClear && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => trackAndChange('')}>
              <XCircleIcon className="size-5" />
            </button>
          )}
        </div>
      </div>
    );
  }
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

export default CommandInput;
